import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
  Alert, Button, Descriptions, Segmented, Select, Spin, Table, Tag, Tooltip, Typography, Upload, message, DatePicker,
} from 'antd';
import { InboxOutlined, CloudUploadOutlined, TableOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import api from '../api';
import PaycorState from '../components/PaycorState';
import { UserContext, hasGrant } from '../App';

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;

const fmtMoney = v => v == null
  ? '—'
  : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PrincipalBillUploadPage() {
  const currentUser = useContext(UserContext);
  // Admin or the page grant, matching the route guard and the API. This was
  // Role === 'admin' alone, so a user granted the page reached it and was
  // then told they needed admin: every control on it was gated on the role
  // rather than on being allowed to use the page.
  const canUse      = currentUser?.Role === 'admin' || hasGrant(currentUser, 'principal-bill');

  // view: 'upload' | 'browse'
  const [view, setView] = useState('upload');

  // phase: 'upload' | 'preview' | 'done'
  const [phase,        setPhase]        = useState('upload');
  const [file,         setFile]         = useState(null);
  const [previewing,   setPreviewing]   = useState(false);
  const [confirming,   setConfirming]   = useState(false);
  const [rows,         setRows]         = useState([]);
  const [qaUsers,      setQaUsers]      = useState([]);
  const [qaUsersLoading, setQaUsersLoading] = useState(true);
  const [result,       setResult]       = useState(null);

  // QAUser assignment for unmatched rows, keyed by row index
  const [assignments, setAssignments] = useState({});

  // Unlinked members (invoice rows with no QAUser link) shown after upload
  const [unlinked,        setUnlinked]        = useState([]);
  const [uploadedMemberIds, setUploadedMemberIds] = useState([]);
  const [unlinkedLoading, setUnlinkedLoading] = useState(false);
  // 'file' = only members this upload touched, 'all' = every outstanding one
  const [unlinkedScope,   setUnlinkedScope]   = useState('file');
  const [linkDraft,       setLinkDraft]       = useState({});
  const [savingLinks,     setSavingLinks]     = useState(new Set());

  // Month inferred from the file's current-coverage rows, overridable
  const [invoiceMonth,      setInvoiceMonth]      = useState(null);
  const [invoiceMonthCands, setInvoiceMonthCands] = useState([]);

  // ── Browse uploaded bills ──────────────────────────────────────────────────
  // Filter by the month a charge covers, or the invoice it arrived on
  const [filterBy,        setFilterBy]        = useState('charge_month');
  const [months,          setMonths]          = useState([]);
  const [skippedLabels,   setSkippedLabels]   = useState([]);
  const [invoiceMonths,   setInvoiceMonths]   = useState([]);
  const [monthsLoading,   setMonthsLoading]   = useState(false);

  // By Member: flat rows from the API, pivoted here so the month columns follow
  // whatever invoice months are present. Mirrors the BCBS view.
  const [byMember,        setByMember]        = useState([]);
  const [byMemberLoading, setByMemberLoading] = useState(false);
  const [memberFrom,      setMemberFrom]      = useState(null);
  const [memberTo,        setMemberTo]        = useState(null);
  const [browseMonth,     setBrowseMonth]     = useState(null);
  const [browseInvoice,   setBrowseInvoice]   = useState(null);
  const [invoices,        setInvoices]        = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  useEffect(() => {
    if (view !== 'browse') return;
    setMonthsLoading(true);
    api.get('/api/admin/principal-bill/months')
      .then(r => {
        const cm = r.data?.charge_months  || [];
        const im = r.data?.invoice_months || [];
        setMonths(cm);
        setInvoiceMonths(im);
        // Default to the newest so the view is never empty on open
        setBrowseMonth(prev   => prev ?? (cm.length ? cm[0] : null));
        setBrowseInvoice(prev => prev ?? (im.length ? im[0] : null));
      })
      .catch(() => { setMonths([]); setInvoiceMonths([]); })
      .finally(() => setMonthsLoading(false));
  }, [view]);

  const activeFilter = filterBy === 'charge_month' ? browseMonth : browseInvoice;

  // Seed the range once to the full span on file, so the view opens showing
  // everything and the pickers narrow it rather than hiding data on first load.
  useEffect(() => {
    if (!invoiceMonths.length || memberFrom || memberTo) return;
    const sorted = [...invoiceMonths].sort();
    setMemberFrom(dayjs(`${sorted[0]}-01`));
    setMemberTo(dayjs(`${sorted[sorted.length - 1]}-01`));
  }, [invoiceMonths, memberFrom, memberTo]);

  useEffect(() => {
    if (view !== 'browse' || filterBy !== 'member') return;
    setByMemberLoading(true);
    api.get('/api/admin/principal-bill/by-member', {
      params: {
        ...(memberFrom ? { month_from: memberFrom.format('YYYY-MM') } : {}),
        ...(memberTo   ? { month_to:   memberTo.format('YYYY-MM')   } : {}),
      },
    })
      .then(r => setByMember(r.data || []))
      .catch(() => setByMember([]))
      .finally(() => setByMemberLoading(false));
  }, [view, filterBy, memberFrom, memberTo]);

  const memberPivot = useMemo(() => {
    const monthsList = [...new Set(byMember.map(r => r.InvoiceMonth).filter(Boolean))].sort();
    // Transaction types come from the data, so a new one appears without a
    // code change.
    const kinds = [...new Set(byMember.map(r => r.TransactionType))].sort((a, b) => a.localeCompare(b));
    const byKey = {};
    byMember.forEach(r => {
      if (!byKey[r.MemberID]) {
        byKey[r.MemberID] = {
          MemberID: r.MemberID, MemberName: r.MemberName,
          QAUserName: r.QAUserName,
          PaycorStatus: r.PaycorStatus, PaycorTermDate: r.PaycorTermDate,
          PaycorHireDate: r.PaycorHireDate,
          Total: 0,
        };
      }
      byKey[r.MemberID][`${r.InvoiceMonth}||${r.TransactionType}`] =
        (byKey[r.MemberID][`${r.InvoiceMonth}||${r.TransactionType}`] || 0) + r.Amount;
      byKey[r.MemberID][`__m__${r.InvoiceMonth}`] =
        (byKey[r.MemberID][`__m__${r.InvoiceMonth}`] || 0) + r.Amount;
      byKey[r.MemberID].Total += r.Amount;
    });
    const rows = Object.values(byKey).sort((a, b) =>
      (a.MemberName || '').localeCompare(b.MemberName || ''));
    return { months: monthsList, kinds, rows };
  }, [byMember]);

  // ── Excel export ──────────────────────────────────────────────────────────
  // A workbook of named sheets; each sheet is plain objects with the header as
  // the key, the same shape the exports elsewhere in the app use. Sheets with
  // no rows are dropped rather than written empty.
  const xlsxDownload = (sheets, filename) => {
    const wb = XLSX.utils.book_new();
    let any = false;
    sheets.forEach(({ name, rows }) => {
      if (!rows.length) return;
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name);
      any = true;
    });
    if (!any) { message.warning('No data to export.'); return; }
    XLSX.writeFile(wb, filename);
  };

  const exportExcel = () => {
    if (filterBy === 'member') {
      const pivot = memberRows.map(r => {
        const row = {
          'Member':            r.MemberName || '',
          'Member ID':         r.MemberID,
          'QA User':           r.QAUserName || 'Unattributed',
          'Employment Status': r.PaycorStatus || '',
          'Term Date':         r.PaycorTermDate || '',
          'Hire Date':         r.PaycorHireDate || '',
        };
        // Invoice months, matching the columns on screen.
        memberPivot.months.forEach(m => { row[m] = r[`__m__${m}`] ?? null; });
        row['Total'] = r.Total;
        return row;
      });
      // The month cells also carry a transaction split that one total per month
      // cannot hold, so the flat rows behind the pivot ride along on their own
      // sheet instead of being dropped.
      const keep = new Set(memberRows.map((r) => r.MemberID));
      const detail = byMember.filter((r) => keep.has(r.MemberID)).map(r => ({
        'Member':            r.MemberName || '',
        'Member ID':         r.MemberID,
        'QA User':           r.QAUserName || 'Unattributed',
        'Employment Status': r.PaycorStatus || '',
        'Term Date':         r.PaycorTermDate || '',
        'Hire Date':         r.PaycorHireDate || '',
        'Invoice Month':     r.InvoiceMonth,
        'Transaction':       r.TransactionType,
        'Amount':            r.Amount,
      }));
      const span = [memberFrom && memberFrom.format('YYYY-MM'), memberTo && memberTo.format('YYYY-MM')]
        .filter(Boolean).join('_to_');
      xlsxDownload(
        [{ name: 'By Member', rows: pivot }, { name: 'Detail', rows: detail }],
        `principal_by_member${span ? `_${span}` : ''}.xlsx`,
      );
      return;
    }

    xlsxDownload([{
      name: 'Invoice Rows',
      rows: invoices.map(r => ({
        'Member':            r.MemberName || '',
        'Member ID':         r.MemberID,
        'QA User':           r.QAUserName || 'Unattributed',
        'Employment Status': r.PaycorStatus || '',
        'Term Date':         r.PaycorTermDate || '',
        'Transaction':       r.Transaction || '',
        'Charge Month':      r.ChargeMonth || '',
        'Invoice Month':     r.InvoiceMonth || '',
        'Division':          r.Division || '',
        'Total Premium':     r.TotalPremium,
        'Uploaded':          r.UploadedAt ? String(r.UploadedAt).replace('T', ' ').substring(0, 16) : '',
      })),
    }], `principal_invoices_${activeFilter || 'all'}.xlsx`);
  };

  // Optional hire-date window over the By Member rows. Held as strings because
  // PaycorHireDate is an ISO date and ISO dates compare correctly as text.
  //
  // A member with no hire date drops out as soon as either bound is set: an
  // unlinked member, or one whose QA user has no Paycor record, has no hire
  // date to fall inside the window. That is the honest answer to "hired
  // between these dates" rather than a convenience, so the count beside the
  // picker says how many were set aside.
  const [hireFrom, setHireFrom] = useState(null);
  const [hireTo,   setHireTo]   = useState(null);

  const memberRows = useMemo(() => {
    const lo = hireFrom?.format('YYYY-MM-DD');
    const hi = hireTo?.format('YYYY-MM-DD');
    if (!lo && !hi) return memberPivot.rows;
    return memberPivot.rows.filter((r) => {
      const d = r.PaycorHireDate;
      if (!d) return false;
      if (lo && d < lo) return false;
      if (hi && d > hi) return false;
      return true;
    });
  }, [memberPivot.rows, hireFrom, hireTo]);

  const hireHidden = memberPivot.rows.length - memberRows.length;

  const exportDisabled = filterBy === 'member'
    ? memberRows.length === 0
    : invoices.length === 0;




  useEffect(() => {
    if (view !== 'browse' || !activeFilter) { setInvoices([]); return; }
    setInvoicesLoading(true);
    const params = filterBy === 'charge_month'
      ? { charge_month: activeFilter }
      : { invoice_month: activeFilter };
    api.get('/api/admin/principal-bill/invoices', { params })
      .then(r => setInvoices(r.data || []))
      .catch(() => setInvoices([]))
      .finally(() => setInvoicesLoading(false));
  }, [view, filterBy, activeFilter]);

  const loadUnlinked = (scope = unlinkedScope, fileIds = null) => {
    setUnlinkedLoading(true);
    // Scoped to the members in the file just uploaded unless asked for all,
    // so the count under the summary matches the "saved unlinked" figure
    // rather than every member outstanding since the first bill.
    const ids = scope === 'file' ? (fileIds ?? uploadedMemberIds) : [];
    const params = ids.length ? { member_ids: ids.join(',') } : undefined;
    api.get('/api/admin/principal-bill/unlinked', { params })
      .then(r => setUnlinked(r.data || []))
      .catch(() => setUnlinked([]))
      .finally(() => setUnlinkedLoading(false));
  };

  const saveLink = async (memberId) => {
    const qaUserId = linkDraft[memberId];
    if (!qaUserId) return;
    setSavingLinks(prev => new Set([...prev, memberId]));
    try {
      await api.post('/api/admin/principal-bill/link', {
        member_id: memberId, qa_user_id: qaUserId,
      });
      message.success('Member linked. All their invoice rows are now claimed.');
      setUnlinked(prev => prev.filter(u => u.MemberID !== memberId));
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to link member.');
    } finally {
      setSavingLinks(prev => { const s = new Set(prev); s.delete(memberId); return s; });
    }
  };

  useEffect(() => {
    api.get('/api/admin/principal-bill/qa-users')
      .then(r => setQaUsers(r.data || []))
      .catch(() => {})
      .finally(() => setQaUsersLoading(false));
  }, []);

  const qaUserOptions = qaUsers.map(u => ({
    label: u.IsActive ? u.DisplayName : `${u.DisplayName} (inactive)`,
    value: u.ID,
  }));

  const handlePreview = async () => {
    if (!file) { message.warning('Select a file first.'); return; }
    setPreviewing(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/api/admin/principal-bill/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data.rows || [];
      setRows(data);
      setInvoiceMonth(res.data.invoice_month || null);
      setInvoiceMonthCands(res.data.invoice_month_candidates || []);
      setSkippedLabels(res.data.skipped_labels || []);
      // Assignments are keyed by MemberID, not row index — a member ID belongs
      // to one person, so all of their lines share a single selection.
      const init = {};
      data.forEach(r => { if (r.QAUserID) init[r.MemberID] = r.QAUserID; });
      setAssignments(init);
      setPhase('preview');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to parse file.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    const payload = rows.map(r => ({ ...r, QAUserID: assignments[r.MemberID] ?? null }));
    try {
      const res = await api.post('/api/admin/principal-bill/confirm', {
        rows: payload, invoice_month: invoiceMonth,
      });
      setResult(res.data);
      setPhase('done');
      const fileIds = [...new Set(payload.map(r => r.MemberID).filter(Boolean))];
      setUploadedMemberIds(fileIds);
      setUnlinkedScope('file');
      loadUnlinked('file', fileIds);
    } catch (err) {
      message.error(err.response?.data?.error || 'Upload failed.');
    } finally {
      setConfirming(false);
    }
  };

  const reset = () => {
    setPhase('upload');
    setFile(null);
    setRows([]);
    setAssignments({});
    setResult(null);
  };

  const browseTotal = invoices.reduce((s, r) => s + (Number(r.TotalPremium) || 0), 0);
  const browseUnlinkedCount = invoices.filter(r => !r.QAUserName).length;

  const matchedCount   = rows.filter(r => r.matched).length;
  const unmatchedCount = rows.length - matchedCount;
  // Count distinct members still needing a selection, not rows — one member
  // may span several invoice lines but only needs assigning once.
  const unresolvedMembers = [...new Set(
    rows.filter(r => !assignments[r.MemberID]).map(r => r.MemberID)
  )].length;

  // ── Preview table ──────────────────────────────────────────────────────────
  const previewCols = [
    {
      title: 'Member',
      key: 'member',
      width: 200,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{r.MemberName || r.MemberID}</div>
          <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{r.MemberID}</Text>
        </div>
      ),
    },
    {
      title: 'QA User',
      key: 'qaUser',
      width: 240,
      render: (_, r) => {
        if (r.matched) {
          return (
            <div>
              <Tag color="green" style={{ marginBottom: 2 }}>Auto-matched</Tag>
              <div style={{ fontSize: 12, color: '#595959' }}>{r.DisplayName}</div>
            </div>
          );
        }
        return (
          <Select
            size="small"
            style={{ width: '100%' }}
            placeholder="Select QA user..."
            value={assignments[r.MemberID]}
            options={qaUserOptions}
            showSearch
            filterOption={(input, opt) =>
              (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={v => setAssignments(prev => ({ ...prev, [r.MemberID]: v }))}
            loading={qaUsersLoading}
            allowClear
            onClear={() => setAssignments(prev => {
              const n = { ...prev }; delete n[r.MemberID]; return n;
            })}
          />
        );
      },
    },
    { title: 'Charge Month',   dataIndex: 'ChargeMonth',   key: 'ChargeMonth',   width: 110 },
    { title: 'Transaction',  dataIndex: 'Transaction',  key: 'Transaction',  width: 160 },
    { title: 'Division',     dataIndex: 'Division',     key: 'Division',     width: 130 },
    {
      title: 'Total Premium',
      dataIndex: 'TotalPremium',
      key: 'TotalPremium',
      width: 110,
      align: 'right',
      render: v => fmtMoney(v),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 16, flexWrap: 'wrap' }}>
        <Title level={4} style={{ margin: 0 }}>Principal Bill</Title>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {view === 'upload' && phase !== 'upload' && (
            <Button onClick={reset}>Upload Another</Button>
          )}
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { label: 'Upload',        value: 'upload', icon: <CloudUploadOutlined /> },
              { label: 'View Uploaded', value: 'browse', icon: <TableOutlined /> },
            ]}
          />
        </div>
      </div>

      {!canUse && (
        <Alert type="error" message="You do not have access to this page." style={{ marginBottom: 16 }} showIcon />
      )}

      {/* ── UPLOAD PHASE ──────────────────────────────────────────────────── */}
      {view === 'upload' && phase === 'upload' && canUse && (
        <>
          <Paragraph type="secondary" style={{ marginBottom: 20 }}>
            Upload a Principal insurance bill in tab-delimited (.txt) or comma-delimited (.tsv/.csv) format.
            The file must contain a header row with at minimum a <strong>Member ID</strong> column.
            Members who have been matched to a QA user in a previous upload are auto-matched;
            new members will need to be assigned manually before confirming.
          </Paragraph>

          <Dragger
            name="file"
            accept=".txt,.tsv,.csv"
            beforeUpload={f => { setFile(f); return false; }}
            onRemove={() => setFile(null)}
            fileList={file ? [file] : []}
            style={{ marginBottom: 16 }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag a Principal bill file here</p>
            <p className="ant-upload-hint">Accepts .txt (tab-delimited) or .csv/.tsv. Must include a "Member ID" column.</p>
          </Dragger>

          <Button
            type="primary"
            onClick={handlePreview}
            loading={previewing}
            disabled={!file}
          >
            Preview & Match
          </Button>
        </>
      )}

      {/* ── PREVIEW PHASE ─────────────────────────────────────────────────── */}
      {view === 'upload' && phase === 'preview' && (
        <>
          <Alert
            type={invoiceMonth ? 'info' : 'warning'}
            showIcon
            style={{ marginBottom: 16 }}
            message={
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span>Invoice month:</span>
                <Select
                  size="small"
                  style={{ minWidth: 130 }}
                  value={invoiceMonth}
                  onChange={setInvoiceMonth}
                  placeholder="Not detected"
                  options={(invoiceMonthCands.length
                    ? invoiceMonthCands
                    : [...new Set(rows.map(r => r.ChargeMonth).filter(Boolean))]
                  ).map(m => ({ label: m, value: m }))}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {invoiceMonth
                    ? 'Taken from the rows with a blank Transaction, which bill the current month. Adjustments keep their own charge month.'
                    : 'No rows with a blank Transaction, so the invoice month could not be inferred — pick one.'}
                </Text>
                {invoiceMonthCands.length > 1 && (
                  <Tag color="orange">
                    {invoiceMonthCands.length} candidates — confirm this is right
                  </Tag>
                )}
              </div>
            }
          />

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
            <Tag color="green">{matchedCount} auto-matched</Tag>
            {skippedLabels.length > 0 && (
              <Tooltip title={skippedLabels.join(', ')}>
                <Tag>{skippedLabels.length} trailer line{skippedLabels.length > 1 ? 's' : ''} skipped</Tag>
              </Tooltip>
            )}
            {unmatchedCount > 0
              ? <Tag color="orange">{unmatchedCount} need assignment</Tag>
              : null
            }
            <Text type="secondary">{rows.length} total rows</Text>

            {unresolvedMembers > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ flex: 1, padding: '4px 12px' }}
                message={`${unresolvedMembers} member${unresolvedMembers > 1 ? 's' : ''} still need a QA user assigned. You may leave them blank and link them afterwards.`}
              />
            )}
          </div>

          <Table
            rowKey={(_, i) => i}
            dataSource={rows}
            columns={previewCols}
            size="small"
            pagination={false}
            scroll={{ x: 'max-content', y: 600 }}
            bordered
            rowClassName={r => !assignments[r.MemberID] ? 'ant-table-row-warning' : ''}
            style={{ marginBottom: 16 }}
          />

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Button
              type="primary"
              onClick={handleConfirm}
              loading={confirming}
            >
              Confirm Upload ({rows.length} rows)
            </Button>
            <Button onClick={reset}>Cancel</Button>
          </div>
        </>
      )}

      {/* ── DONE PHASE ────────────────────────────────────────────────────── */}
      {view === 'upload' && phase === 'done' && result && (
        <>
          <Alert
            type={(result.skipped > 0 || result.collided > 0) ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 20 }}
            message={
              (result.skipped > 0 || result.collided > 0)
                ? 'Upload finished, but not every row was stored'
                : 'Upload complete'
            }
            description={
              <>
                <Descriptions size="small" column={3} style={{ marginTop: 8 }}>
                  <Descriptions.Item label="Rows in file">{result.received ?? rows.length}</Descriptions.Item>
                  <Descriptions.Item label="Rows saved">{result.upserted}</Descriptions.Item>
                  <Descriptions.Item label="Linked to QA user">
                    {result.upserted - (result.unlinked ?? 0)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Saved unlinked">
                    {result.unlinked > 0
                      ? <Tag color="orange">{result.unlinked}</Tag>
                      : <Tag color="green">0</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Skipped">
                    {result.skipped > 0
                      ? <Tag color="red">{result.skipped}</Tag>
                      : <Tag color="green">0</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Overwrote a twin">
                    {result.collided > 0
                      ? <Tag color="red">{result.collided}</Tag>
                      : <Tag color="green">0</Tag>}
                  </Descriptions.Item>
                </Descriptions>

                {result.collided > 0 && (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {result.collided} row{result.collided > 1 ? 's' : ''} shared a
                    Member ID + Charge Month + Transaction + Total Premium + Invoice Month
                    with another row in this file, so only the last of each was kept. That
                    combination is not unique for this bill and needs another column to tell
                    those lines apart.
                  </Text>
                )}
              </>
            }
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
            <Title level={5} style={{ margin: 0 }}>
              Unassigned Members
              {unlinked.length > 0 && (
                <Tag color="orange" style={{ marginLeft: 8 }}>{unlinked.length}</Tag>
              )}
            </Title>
            <Segmented
              size="small"
              value={unlinkedScope}
              onChange={(v) => { setUnlinkedScope(v); loadUnlinked(v); }}
              options={[
                { label: 'From this upload', value: 'file' },
                { label: 'All outstanding',  value: 'all'  },
              ]}
            />
          </div>
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            {unlinkedScope === 'file'
              ? 'Member IDs from the file you just uploaded that are not linked to a QA user.'
              : 'Every Member ID with invoice rows that is not linked to a QA user, across all bills uploaded.'}
            {' '}Assigning one here claims <strong>every</strong> invoice row for that member,
            including bills uploaded previously.
          </Paragraph>

          {unlinkedLoading ? <Spin /> : unlinked.length === 0 ? (
            <Alert
              type="success"
              showIcon
              message="Every member on file is linked to a QA user."
              style={{ marginBottom: 20 }}
            />
          ) : (
            <Table
              rowKey="MemberID"
              dataSource={unlinked}
              size="small"
              pagination={false}
              bordered
              style={{ marginBottom: 20 }}
              scroll={{ x: 'max-content' }}
              columns={[
                {
                  title: 'Member',
                  key: 'member',
                  width: 200,
                  render: (_, r) => (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{r.MemberName || '(no name)'}</div>
                      <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{r.MemberID}</Text>
                    </div>
                  ),
                },
                {
                  title: 'Assign To',
                  key: 'assign',
                  width: 240,
                  render: (_, r) => (
                    <Select
                      size="small"
                      style={{ width: '100%' }}
                      placeholder="Select QA user..."
                      value={linkDraft[r.MemberID]}
                      options={qaUserOptions}
                      showSearch
                      filterOption={(input, opt) =>
                        (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      onChange={v => setLinkDraft(prev => ({ ...prev, [r.MemberID]: v }))}
                      loading={qaUsersLoading}
                      allowClear
                    />
                  ),
                },
                {
                  title: '',
                  key: 'save',
                  width: 80,
                  render: (_, r) => (
                    <Button
                      size="small"
                      type="primary"
                      disabled={!linkDraft[r.MemberID]}
                      loading={savingLinks.has(r.MemberID)}
                      onClick={() => saveLink(r.MemberID)}
                    >
                      Link
                    </Button>
                  ),
                },
                {
                  title: 'Charge Months',
                  key: 'months',
                  width: 140,
                  render: (_, r) => r.FirstMonth === r.LastMonth
                    ? r.FirstMonth
                    : `${r.FirstMonth} – ${r.LastMonth}`,
                },
                { title: 'Rows', dataIndex: 'InvoiceRows', key: 'InvoiceRows', width: 70, align: 'right' },
                {
                  title: 'Total Premium',
                  dataIndex: 'TotalPremium',
                  key: 'TotalPremium',
                  width: 120,
                  align: 'right',
                  render: v => fmtMoney(v),
                },
              ]}
            />
          )}

          <Button type="primary" onClick={reset}>Upload Another Bill</Button>
        </>
      )}

      {/* ── VIEW UPLOADED ─────────────────────────────────────────────────── */}
      {view === 'browse' && canUse && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <Segmented
              size="small"
              value={filterBy}
              onChange={setFilterBy}
              options={[
                { label: 'By Charge Month',  value: 'charge_month'  },
                { label: 'By Invoice Month', value: 'invoice_month' },
                { label: 'By Member',        value: 'member' },
              ]}
            />
            {filterBy === 'member' && (
              <>
                <DatePicker
                  picker="month" value={memberFrom} onChange={setMemberFrom}
                  format="MMM YYYY" placeholder="From" style={{ width: 130 }}
                  disabledDate={d => memberTo && d && d.isAfter(memberTo, 'month')}
                />
                <Typography.Text type="secondary">to</Typography.Text>
                <DatePicker
                  picker="month" value={memberTo} onChange={setMemberTo}
                  format="MMM YYYY" placeholder="To" style={{ width: 130 }}
                  disabledDate={d => memberFrom && d && d.isBefore(memberFrom, 'month')}
                />
                {/* Optional: clear either end to stop bounding it. */}
                <DatePicker
                  value={hireFrom}
                  onChange={setHireFrom}
                  format="YYYY-MM-DD"
                  placeholder="Hired from"
                  style={{ width: 140 }}
                  disabledDate={(d) => hireTo && d && d.isAfter(hireTo, 'day')}
                />
                <DatePicker
                  value={hireTo}
                  onChange={setHireTo}
                  format="YYYY-MM-DD"
                  placeholder="Hired to"
                  style={{ width: 140 }}
                  disabledDate={(d) => hireFrom && d && d.isBefore(hireFrom, 'day')}
                />
                {hireHidden > 0 && (
                  <Text type="secondary">
                    {hireHidden} hidden (no hire date in range)
                  </Text>
                )}
                {memberRows.length > 0 && (
                  <Typography.Text type="secondary">
                    {memberRows.length} member{memberRows.length === 1 ? '' : 's'}
                  </Typography.Text>
                )}
              </>
            )}
            {filterBy === 'member' ? null : monthsLoading ? <Spin size="small" /> : filterBy === 'invoice_month' ? (
              <Select
                style={{ minWidth: 160 }}
                placeholder="Select invoice month"
                value={browseInvoice}
                onChange={setBrowseInvoice}
                options={invoiceMonths.map(m => ({ label: m, value: m }))}
                showSearch
                notFoundContent="No invoice months recorded"
              />
            ) : (
              <Select
                style={{ minWidth: 160 }}
                placeholder="Select month"
                value={browseMonth}
                onChange={setBrowseMonth}
                options={months.map(m => ({ label: m, value: m }))}
                showSearch
                notFoundContent="No bills uploaded yet"
              />
            )}
            {invoices.length > 0 && (
              <>
                <Text type="secondary">{invoices.length} rows</Text>
                {browseUnlinkedCount > 0 && (
                  <Tag color="orange">{browseUnlinkedCount} unattributed</Tag>
                )}
                <Text strong style={{ marginLeft: 'auto' }}>
                  Total: {fmtMoney(browseTotal)}
                </Text>
              </>
            )}
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={exportExcel}
              disabled={exportDisabled}
            >
              Export Excel
            </Button>
          </div>

          {filterBy === 'member' && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Month columns are <strong>invoice months</strong>, not charge months.
            </Text>
          )}

          {filterBy === 'member' ? (
            byMemberLoading ? <Spin /> : memberPivot.rows.length === 0 ? (
              <Alert type="info" showIcon message="No invoice rows in this range." />
            ) : (
              <Table
                rowKey="MemberID"
                dataSource={memberRows}
                size="small"
                bordered
                pagination={false}
                sticky
                scroll={{ x: 'max-content' }}
                columns={[
                  {
                    title: 'Member', key: 'member', width: 200, fixed: 'left',
                    render: (_, r) => (
                      <div>
                        <Text strong>{r.MemberName}</Text>
                        <div style={{ fontSize: 11, color: '#999' }}>{r.MemberID}</div>
                      </div>
                    ),
                  },
                  {
                    title: 'QA User', key: 'qaUser', width: 190, fixed: 'left',
                    render: (_, r) => (
                      <div>
                        {r.QAUserName ? (
                          <span>
                            {r.QAUserName}
                          </span>
                        ) : <Tag color="orange">Unattributed</Tag>}
                        <PaycorState status={r.PaycorStatus} termDate={r.PaycorTermDate} />
                        {/* Always shown when known, unlike the state tag above:
                            when someone started is context for every row, not
                            just the ones worth chasing. The rehire date when it
                            is later than the hire date, otherwise the hire. */}
                        {r.PaycorHireDate && (
                          <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                            Hired {r.PaycorHireDate}
                          </div>
                        )}
                      </div>
                    ),
                  },
                  // One column per invoice month holding that month's total, with
                  // the transaction split inside the cell rather than as extra
                  // columns -- the same shape as the BCBS By Member view.
                  ...memberPivot.months.map(m => ({
                    title: m, key: m, width: 150, align: 'right',
                    render: (_, r) => {
                      const parts = memberPivot.kinds
                        .map(k => ({ label: k, v: r[`${m}||${k}`] }))
                        .filter(x => x.v);
                      if (!parts.length) return <span style={{ color: '#d9d9d9' }}>-</span>;
                      const total = r[`__m__${m}`];
                      return (
                        <div style={{ lineHeight: 1.3 }}>
                          <div><Text strong>{fmtMoney(parts.length === 1 ? parts[0].v : total)}</Text></div>
                          {parts.map(x => (
                            <div key={x.label} style={{ fontSize: 11, color: '#8c8c8c' }}>
                              {x.label}{parts.length > 1 ? ` ${fmtMoney(x.v)}` : ''}
                            </div>
                          ))}
                        </div>
                      );
                    },
                  })),
                  {
                    title: 'Total', dataIndex: 'Total', key: 'Total',
                    width: 120, align: 'right', fixed: 'right',
                    sorter: (a, b) => a.Total - b.Total,
                    render: v => <Text strong>{fmtMoney(v)}</Text>,
                  },
                ]}
              />
            )
          ) : invoicesLoading ? <Spin /> : !activeFilter ? (
            <Text type="secondary">Select a month to view its invoice rows.</Text>
          ) : invoices.length === 0 ? (
            <Alert type="info" showIcon message={`No invoice rows stored for ${activeFilter}.`} />
          ) : (
            <Table
              rowKey="ID"
              dataSource={invoices}
              size="small"
              pagination={false}
              bordered
              scroll={{ x: 'max-content', y: 600 }}
              columns={[
                {
                  title: 'Member',
                  key: 'member',
                  width: 210,
                  render: (_, r) => (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{r.MemberName || '(no name)'}</div>
                      <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{r.MemberID}</Text>
                    </div>
                  ),
                },
                {
                  title: 'QA User',
                  key: 'qaUser',
                  width: 200,
                  render: (_, r) => r.QAUserName
                    ? (
                      <div>
                        <span>
                          {r.QAUserName}
                        </span>
                        <PaycorState status={r.PaycorStatus} termDate={r.PaycorTermDate} />
                      </div>
                    )
                    : <Tag color="orange">Unattributed</Tag>,
                },
                { title: 'Transaction', dataIndex: 'Transaction', key: 'Transaction', width: 160,
                  render: v => v || <Text type="secondary">—</Text> },
                { title: 'Charge Month', dataIndex: 'ChargeMonth', key: 'ChargeMonth', width: 110,
                  render: v => v || <Text type="secondary">—</Text> },
                { title: 'Invoice Month', dataIndex: 'InvoiceMonth', key: 'InvoiceMonth', width: 115,
                  render: (v, r) => v
                    ? <span style={{ color: v !== r.ChargeMonth ? '#d46b08' : undefined }}>{v}</span>
                    : <Text type="secondary">—</Text> },
                { title: 'Division', dataIndex: 'Division', key: 'Division', width: 140,
                  render: v => v || <Text type="secondary">—</Text> },
                {
                  title: 'Total Premium',
                  dataIndex: 'TotalPremium',
                  key: 'TotalPremium',
                  width: 120,
                  align: 'right',
                  render: v => fmtMoney(v),
                },
                {
                  title: 'Uploaded',
                  dataIndex: 'UploadedAt',
                  key: 'UploadedAt',
                  width: 150,
                  render: v => v ? String(v).replace('T', ' ').substring(0, 16) : '—',
                },
              ]}
            />
          )}
        </>
      )}
    </>
  );
}
